/**
 * Interactive root component.
 */

/** Dependencies -------------------------------------------- */

// External

import { debounce } from 'throttle-debounce'
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll'

import gsap from 'gsap'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'

gsap.registerPlugin( DrawSVGPlugin )

// Internal

import { getPlugin } from '../dictionary'
import { ucFirst, getCssVar } from '../../helpers'

/** Main ---------------------------------------------------- */

export default class App {

	/** Setup ----------------------------------------------- */

	// Props

	plugin                = null

	sideBySideThreshold   = 1024
	resizeDebounce        = 100

	section               = null
	connections           = null
	innerCircleConnection = null
	outerCircleConnection = null
	lineConnections       = null
	nodes                 = null
	triggers              = null
	ibcAssetContainer     = null
	ibcAsset              = null
	ibcNodeLabel          = null
	hospitalAsset         = null
	hospitalNodeLabel     = null
	homeAsset             = null
	homeNodeLabel         = null
	clinicNodeLabel       = null
	clinicAsset           = null
	popovers              = null
	backButtons           = null
	timeline              = null

	// State

	haveEventBindingsBeenSet = false
	activeNode               = null

	// Util

	viewportWidth            = 0

	constructor() {
		this.plugin  = getPlugin()
		this.section = document.querySelector( `[data-${this.plugin.htmlSlug}]` )
	}

	/** Initialization -------------------------------------- */

	init() {

		if ( ! this.section ) return

		this.connections           = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-connections]` )
		this.innerCircleConnection = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-connection="inner-circle"]` )
		this.outerCircleConnection = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-connection="outer-circle"]` )
		this.lineConnections       = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-connection="line"]` )
		this.nodes                 = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-node]` )
		this.triggers              = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-trigger]` )
		this.ibcAssetContainer     = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-asset-container="ibc"]` )
		this.ibcAsset              = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-asset="ibc"]` )
		this.ibcNodeLabel          = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-node-label="ibc"]` )
		this.hospitalAsset         = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-asset="hospital"]` )
		this.hospitalNodeLabel     = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-node-label="hospital"]` )
		this.homeAsset             = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-asset="home"]` )
		this.homeNodeLabel         = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-node-label="home"]` )
		this.clinicAsset           = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-asset="clinic"]` )
		this.clinicNodeLabel       = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-node-label="clinic"]` )
		this.popovers              = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-popover]` )
		this.popoverInners         = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-popover-inner]` )
		this.backButtons           = this.section.querySelectorAll( `[data-${this.plugin.htmlSlug}-back-button]` )

		this.setViewportWidth()
		this.animate()
	}

	/** Events ---------------------------------------------- */

	handleTriggerFocusy( e ) {
		this.setElementInteractionState( this.getNodeFromNodePointer( e.currentTarget ) )
	}

	handleTriggerUnfocusy( e ) {
		this.setElementInteractionState( this.getNodeFromNodePointer( e.currentTarget ), false )
	}

	handleTriggerClick( e ) {
		e.stopPropagation()
		this.setActiveNode( e.currentTarget )
	}

	handleBackButtonClick( e ) {
		e.stopPropagation()
		this.setActiveNode()
	}

	handlePopoverClickOutside( e ) {

		for ( let popoverInner of this.popoverInners ) {
			if ( popoverInner.contains( e.target ) ) return
		}

		this.setActiveNode()
	}

	handleResize() {

		if ( window.innerWidth === this.viewportWidth ) return

		this.setViewportWidth()
		this.setActiveNode()
		this.animate()
	}

	setEventBindings() {

		if ( this.haveEventBindingsBeenSet ) return

		for ( let trigger of this.triggers ) {
			trigger.addEventListener( 'mouseover', this.handleTriggerFocusy.bind( this ) )
			trigger.addEventListener( 'focusin', this.handleTriggerFocusy.bind( this ) )
			trigger.addEventListener( 'mouseout', this.handleTriggerUnfocusy.bind( this ) )
			trigger.addEventListener( 'focusout', this.handleTriggerUnfocusy.bind( this ) )
			trigger.addEventListener( 'click', this.handleTriggerClick.bind( this ) )
		}

		for ( let backButton of this.backButtons ) {
			backButton.addEventListener( 'click', this.handleBackButtonClick.bind( this ) )
		}

		document.addEventListener( 'click', this.handlePopoverClickOutside.bind( this ) )
		window.addEventListener( 'resize', debounce( this.resizeDebounce, this.handleResize.bind( this ) ) )

		this.haveEventBindingsBeenSet = true
	}

	/** Main methods ---------------------------------------- */

	animate() {

		// Resetting in case this has happened before

		this.section.classList.remove( 'is-ready' )
		this.section.classList.remove( 'has-animated-content' )		

		if ( this.timeline ) {
			this.timeline.kill()
			this.timeline = null
		}

		const header = document.querySelector( 'header' )

		if ( header ) {
			this.section.style.setProperty( '--headerHeight', `${header.offsetHeight}px` )
		}

		// Initial states

		const isSideBySide = this.isSideBySide()

		const ibcAssetYOffsetToCenterRatio = isSideBySide ? 0.2 : 0.25
		const ibcAssetYOffsetToCenter      = this.ibcAssetContainer.item( 0 ).getBoundingClientRect().height * ibcAssetYOffsetToCenterRatio

		gsap.set( this.innerCircleConnection, { opacity: 0, scale: 0.7, transformOrigin: 'center' } )
		gsap.set( [ this.innerCircleConnection, this.lineConnections, this.outerCircleConnection ], { drawSVG: false } )
		gsap.set( this.outerCircleConnection, { opacity: 0, scale: 0.9, transformOrigin: 'center' } )
		gsap.set( this.ibcAssetContainer, { opacity: 0, y: ibcAssetYOffsetToCenter, scale: 0.4 } )
		gsap.set( [ this.hospitalAsset, this.homeAsset, this.clinicAsset ], { y: '100%' } )
		gsap.set( [ this.ibcNodeLabel, this.hospitalNodeLabel, this.homeNodeLabel, this.clinicNodeLabel ], { y: '-100%' } )

		// Animate in

		const interactiveStartDelay = isSideBySide ? '+0.3' : '+0.1'
		const contentStartLabel     = isSideBySide ? 'start' : 'nodeLabels'
		
		const connectionDashLength = getCssVar( 'abcPiConnectionStrokeDashLength', this.section )
		const connectionDashGap    = getCssVar( 'abcPiConnectionStrokeDashGap', this.section )
		const connectionDasharray  = `${connectionDashLength} ${connectionDashGap}`

		const toDashArrayProps = { strokeDasharray: connectionDasharray, duration: 0.8, ease: 'circ.out' }
		const toNodeLabelProps = { y: 0, duration: 0.4, ease: 'circ.out', stagger: 0.1 }

		this.timeline = gsap.timeline( { delay: 1, paused: true, onComplete: this.setEventBindings.bind( this ) } )

		this.timeline
			.addLabel( 'start' )
			.call( () => this.section.classList.add( 'is-ready' ), [], 'start' )
			.addLabel( 'interactiveStart', `>${interactiveStartDelay}` )
			.to( [ this.clinicAsset, this.hospitalAsset, this.homeAsset ], { y: 0, duration: 1.2, ease: 'circ.out', stagger: 0.3 }, 'interactiveStart' )
			.addLabel( 'ibcAsset', '>-1.3' )
			.to( this.ibcAssetContainer, { opacity: 1, scale: 1, duration: 0.9, ease: 'back.out( 3 )' }, 'ibcAsset' )
			.addLabel( 'innerCircle', '>-0.5' )
			.to( this.innerCircleConnection, { opacity: 1, scale: 1, drawSVG: true, duration: 0.9, ease: 'power2.out' }, 'innerCircle' )
			.addLabel( 'lineConnections', '>-0.5' )
			.to( this.lineConnections, { drawSVG: true, duration: 0.5, ease: 'none' }, 'lineConnections' )
			.to( [ this.innerCircleConnection, this.lineConnections ], toDashArrayProps )
			.addLabel( 'outerCircle', '>-2.8' )
			.to( this.outerCircleConnection, { drawSVG: true, duration: 2, ease: 'power1.out' }, 'outerCircle' )
			.addLabel( 'outerCircleDraw', '>' )
			.to( this.outerCircleConnection, { opacity: 1, scale: 1, duration: 1.2, ease: 'power2.out' }, '>-0.5' )
			.to( this.outerCircleConnection, { ...toDashArrayProps, duration: 1 }, 'outerCircleDraw' )
			.addLabel( 'nodeLabels', `>${isSideBySide ? '+0.3' : '-0.1'}` )
			.to( [ this.clinicNodeLabel, this.hospitalNodeLabel, this.homeNodeLabel, this.ibcNodeLabel ], toNodeLabelProps, 'nodeLabels' )
			.to( this.ibcAssetContainer, { y: isSideBySide ? 0 : ibcAssetYOffsetToCenter, duration: toNodeLabelProps.duration }, `>-${toNodeLabelProps.duration}` )
			.call( () => this.section.classList.add( 'has-animated-content' ), [], contentStartLabel )
			.play()
	}

	setActiveNode( targetNode = null ) {
		
		const nonTargetNodes = Array.from( this.nodes ).filter( node => ! node.isEqualNode( targetNode ) )

		for ( let nonTargetNode of nonTargetNodes ) {
			this.tapNode( nonTargetNode, false )
		}

		this.setScrollLock( targetNode )
		this.section.classList[ targetNode ? 'add' : 'remove']( 'has-active-node' )

		if ( ! targetNode ) {
			this.activeNode = null
			return
		}

		this.activeNode = targetNode
		this.tapNode( targetNode )
	}

	tapNode( targetNode, isActive = true ) {

		const targetPopover = this.getPopoverFromNodePointer( targetNode )

		this.setElementActiveState( targetNode, isActive )
		this.setElementActiveState( targetPopover, isActive )
	}

	setElementActiveState( element, isActive = true ) {
		element.classList.add( isActive ? 'is-active' : 'is-inactive' )
		element.classList.remove( isActive ? 'is-inactive' : 'is-active' )
	}

	setElementInteractionState( element, isInteracted = true ) {
		element.classList[isInteracted ? 'add' : 'remove']( 'is-interacted' )
	}

	/** Utility methods ------------------------------------- */

	getElementFromNodePointer( nodePointer, element = 'node' ) {
		const nodeName = nodePointer.dataset[`${this.plugin.jsSlug}Node`]
		return this.section.querySelector( `[data-${this.plugin.htmlSlug}-${element}][data-${this.plugin.htmlSlug}-node="${nodeName}"]` )
	}

	getNodeFromNodePointer( nodePointer ) {
		return this.getElementFromNodePointer( nodePointer, 'node' )
	}

	getPopoverFromNodePointer( nodePointer ) {
		return this.getElementFromNodePointer( nodePointer, 'popover' )
	}

	isSideBySide() {
		return window.innerWidth >= this.sideBySideThreshold
	}

	setViewportWidth() {
		this.viewportWidth = window.innerWidth
	}

	setScrollLock( isLocked = true ) {

		if ( this.isSideBySide() ) return

		if ( isLocked ) {
			disablePageScroll()
		}
		else {
			enablePageScroll()
		}
	}
}
